/**
 * Global Localization Service
 * 
 * Extends the Coming Soon translation system to be globally available.
 * Provides centralized translation management for all pages.
 * 
 * Follows OCP: Extends existing functionality without modifying it.
 */

export type Language = 'en' | 'es'

/**
 * Global translation keys organized by namespace
 */
export interface GlobalTranslations {
  common: {
    loading: string
    error: string
    success: string
    cancel: string
    save: string
    delete: string
    edit: string
    view: string
    back: string
    next: string
    previous: string
    search: string
    filter: string
    sort: string
  }
  navigation: {
    home: string
    products: string
    auctions: string
    enterprise: string
    government: string
    about: string
    contact: string
    login: string
    register: string
    dashboard: string
  }
  auth: {
    login: {
      title: string
      subtitle: string
      emailLabel: string
      emailPlaceholder: string
      passwordLabel: string
      passwordPlaceholder: string
      rememberMe: string
      forgotPassword: string
      signIn: string
      signingIn: string
      createAccount: string
      or: string
      errors: {
        fillAllFields: string
        invalidEmail: string
        loginFailed: string
        networkError: string
        serviceUnavailable: string
        endpointNotFound: string
        serverError: string
        tooManyAttempts: string
        genericError: string
      }
    }
    forgotPassword: {
      title: string
      subtitle: string
      backToLogin: string
      checkEmail: string
      sentInstructions: string
      returnToLogin: string
      emailLabel: string
      emailPlaceholder: string
      sendResetInstructions: string
      sending: string
    }
    register: {
      createAccount: string
      signInExisting: string
      joinAs: string
      buyer: string
      vendor: string
      firstName: string
      lastName: string
      confirmPassword: string
      companyInfo: string
      businessInfo: string
      companyName: string
      goToLogin: string
      checkEmail: string
    }
  }
  home: {
    hero: {
      title: string
      subtitle: string
      cta: {
        browseProducts: string
        viewAuctions: string
        exploreEnterprise: string
      }
    }
    features: {
      title: string
      subtitle: string
      secureTransactions: {
        title: string
        description: string
      }
      qualityVerified: {
        title: string
        description: string
      }
      bestPrices: {
        title: string
        description: string
      }
    }
    partnership: {
      hero: {
        title: string
        subtitle: string
      }
      benefits: {
        title: string
        reducedFriction: {
          title: string
          description: string
        }
        fasterProcurement: {
          title: string
          description: string
        }
        centralizedRfq: {
          title: string
          description: string
        }
        customWorkflow: {
          title: string
          description: string
        }
        marketVisibility: {
          title: string
          description: string
        }
        costSavings: {
          title: string
          description: string
        }
      }
      modules: {
        title: string
        subtitle: string
        marketplace: {
          title: string
          description: string
          features: string[]
        }
        enterprise: {
          title: string
          description: string
          features: string[]
        }
        government: {
          title: string
          description: string
          features: string[]
        }
      }
      collaboration: {
        title: string
        subtitle: string
        requestFeatures: {
          title: string
          description: string
        }
        influenceWorkflows: {
          title: string
          description: string
        }
        guideRoadmap: {
          title: string
          description: string
        }
        continuousCollaboration: {
          title: string
          description: string
        }
      }
      cta: {
        title: string
        subtitle: string
        explorePartnership: string
        requestDemo: string
        earlyFeedback: string
        contactEmail: string
        contactLabel: string
      }
    }
    about: {
      title: string
      description: string
      ecommerce: {
        title: string
        description: string
      }
      quotation: {
        title: string
        description: string
      }
      contracting: {
        title: string
        description: string
      }
    }
    categories: {
      title: string
      subtitle: string
    }
    featured: {
      title: string
      subtitle: string
      viewAll: string
    }
    cta: {
      enterprise: {
        title: string
        subtitle: string
        exploreEnterprise: string
        viewRfqs: string
      }
      general: {
        title: string
        subtitle: string
        getStarted: string
        exploreProducts: string
      }
    }
  }
  footer: {
    description: string
    quickLinks: string
    browseProducts: string
    liveAuctions: string
    categories: string
    roiCalculator: string
    enterpriseSolutions: string
    forBusiness: string
    becomeVendor: string
    enterpriseBuyers: string
    governmentAgencies: string
    quoteManagement: string
    digitalWallet: string
    legalSupport: string
    termsOfService: string
    privacyPolicy: string
    contactSupport: string
    legalInquiries: string
    helpCenter: string
    followUs: string
    allRightsReserved: string
    empoweringSustainable: string
    legalNotice: string
  }
  products: {
    title: string
    subtitle: string
    searchPlaceholder: string
    category: string
    allCategories: string
    minPrice: string
    maxPrice: string
    condition: string
    anyCondition: string
    new: string
    likeNew: string
    good: string
    fair: string
    poor: string
    noProducts: string
    buyFromVerified: string
    filters: string
    clearFilters: string
    productsFound: string
    tryAdjustingFilters: string
    sortBy: string
    newest: string
    price: string
    name: string
    highToLow: string
    lowToHigh: string
  }
  auctions: {
    title: string
    subtitle: string
    searchPlaceholder: string
    minBid: string
    activeAuction: string
    auctionEnded: string
    bidNow: string
    currentBid: string
    noAuctions: string
    viewDetails: string
    viewResult: string
    buyNowAvailable: string
    activeAuctionsCount: string
  }
  search: {
    title: string
    searchSolarProducts: string
    noResults: string
    searching: string
    resultsFound: string
  }
  categories: {
    pageTitle: string
    pageSubtitle: string
    searchPlaceholder: string
    failedToLoad: string
  }
  productDetail: {
    buyNow: string
    placeBid: string
    addToCart: string
    description: string
    seller: string
    condition: string
    category: string
    backToSearch: string
    productNotFound: string
    productNotFoundDesc: string
    browseProducts: string
    noImageAvailable: string
    currentBid: string
    timeLeft: string
    minimumBid: string
    buyNowPrice: string
    price: string
    messageSeller: string
    specifications: string
    biddingHistory: string
    winning: string
    auctionEnded: string
    failedToLoad: string
  }
  gov: {
    dashboard: string
    rfqs: string
    proposals: string
    vendors: string
    createRfq: string
    settings: string
    registerSuccess: {
      title: string
      defaultMessage: string
      whatHappensNext: string
      step1: string
      step2: string
      step3: string
      goToLogin: string
      backToHome: string
    }
  }
  terms: {
    title: string
    backToEvoltbay: string
    lastUpdated: string
  }
  privacy: {
    title: string
    backToEvoltbay: string
    lastUpdated: string
  }
  roi: {
    title: string
    subtitle: string
    loginRequired: string
    loginRequiredDesc: string
    enterpriseVendorTitle: string
    enterpriseVendorDesc: string
    goToVendorDirectory: string
    signIn: string
  }
  nav: {
    dashboard: string
    rfqs: string
    proposals: string
    vendors: string
    browse: string
    auctions: string
    categories: string
    enterprise: string
    vendorDirectory: string
    roiSimulator: string
    browseRfqs: string
    myResponses: string
    myRfqs: string
    myQuotes: string
    backToEvoltbay: string
    sell: string
    settings: string
    signOut: string
    signingOut: string
    agencyDashboard: string
    enterpriseDashboard: string
    wallet: string
    profiles: string
    company: string
    personal: string
    profile: string
    messages: string
    adminDashboard: string
    adminSettings: string
    govAgencyApprovals: string
    myOrders: string
    myProducts: string
    signIn: string
    signUp: string
    searchProducts: string
    cart: string
  }
  partnership: {
    title: string
    subtitle: string
    collaborativeDevelopment: {
      title: string
      description: string
    }
    directCollaboration: {
      title: string
      description: string
    }
    featureRequests: {
      title: string
      description: string
    }
    adaptiveSystem: {
      title: string
      description: string
    }
    cta: {
      title: string
      description: string
      button: string
      subtext: string
    }
  }
}

const translations: Record<Language, GlobalTranslations> = {
  en: {
    common: {
      loading: 'Loading...',
      error: 'An error occurred',
      success: 'Success',
      cancel: 'Cancel',
      save: 'Save',
      delete: 'Delete',
      edit: 'Edit',
      view: 'View',
      back: 'Back',
      next: 'Next',
      previous: 'Previous',
      search: 'Search',
      filter: 'Filter',
      sort: 'Sort'
    },
    navigation: {
      home: 'Home',
      products: 'Products',
      auctions: 'Auctions',
      enterprise: 'Enterprise',
      government: 'Government',
      about: 'About',
      contact: 'Contact',
      login: 'Login',
      register: 'Register',
      dashboard: 'Dashboard'
    },
    auth: {
      login: {
        title: 'Sign in to your account',
        subtitle: 'Or',
        emailLabel: 'Email address',
        emailPlaceholder: 'Enter your email',
        passwordLabel: 'Password',
        passwordPlaceholder: 'Enter your password',
        rememberMe: 'Remember me',
        forgotPassword: 'Forgot your password?',
        signIn: 'Sign in',
        signingIn: 'Signing in...',
        createAccount: 'create a new account',
        or: 'Or',
        errors: {
          fillAllFields: 'Please fill in all fields',
          invalidEmail: 'Please enter a valid email address',
          loginFailed: 'Login failed',
          networkError: 'Unable to connect to the authentication service. Please check your internet connection and try again.',
          serviceUnavailable: 'Authentication service is temporarily unavailable. Please try again in a moment.',
          endpointNotFound: 'Authentication service endpoint not found. Please contact support if this persists.',
          serverError: 'Server error occurred. Please try again in a moment.',
          tooManyAttempts: 'Too many login attempts. Please wait before trying again.',
          genericError: 'An error occurred during login. Please try again.'
        }
      },
      forgotPassword: {
        title: 'Forgot your password?',
        subtitle: "Enter your email address and we'll send you instructions to reset your password.",
        backToLogin: 'Back to Login',
        checkEmail: 'Check your email',
        sentInstructions: "We've sent password reset instructions to",
        returnToLogin: 'Return to Login',
        emailLabel: 'Email address',
        emailPlaceholder: 'Enter your email',
        sendResetInstructions: 'Send Reset Instructions',
        sending: 'Sending...'
      },
      register: {
        createAccount: 'Create your account',
        signInExisting: 'sign in to your existing account',
        joinAs: 'I want to join Evoltbay as a:',
        buyer: 'Buyer',
        vendor: 'Vendor/Seller',
        firstName: 'First name',
        lastName: 'Last name',
        confirmPassword: 'Confirm Password',
        companyInfo: 'Company Information',
        businessInfo: 'Business Information',
        companyName: 'Company name',
        goToLogin: 'Go to Login',
        checkEmail: 'Check your email'
      }
    },
    home: {
      hero: {
        title: 'Extend Your Solar Ecosystem Through Partnership',
        subtitle: 'EvoltBay is a partnership platform that extends your existing operations—not replacing them. Collaborate with our development team to shape a platform tailored to your business needs.',
        cta: {
          browseProducts: 'Browse Products',
          viewAuctions: 'View Auctions',
          exploreEnterprise: 'Explore Enterprise Solutions'
        }
      },
      features: {
        title: 'Why Choose EvoltBay?',
        subtitle: 'We provide a secure, efficient, and comprehensive platform for all your solar needs.',
        secureTransactions: {
          title: 'Secure Transactions',
          description: 'Advanced encryption and buyer protection ensure your transactions are safe and secure.'
        },
        qualityVerified: {
          title: 'Quality Verified',
          description: 'All products are verified for quality and authenticity by our expert team.'
        },
        bestPrices: {
          title: 'Best Prices',
          description: 'Competitive pricing through auctions and direct sales from verified sellers.'
        }
      },
      partnership: {
        hero: {
          title: 'Built Through Partnership',
          subtitle: 'EvoltBay treats solar companies as strategic partners, not just customers. We collaborate directly with partners to build a platform that extends your ecosystem.'
        },
        benefits: {
          title: 'Partnership Benefits',
          reducedFriction: {
            title: 'Reduced Operational Friction',
            description: 'Streamline your workflows with a platform that adapts to your existing processes instead of forcing change.'
          },
          fasterProcurement: {
            title: 'Faster Procurement Cycles',
            description: 'Accelerate your solar equipment procurement with centralized RFQ management and vendor discovery.'
          },
          centralizedRfq: {
            title: 'Centralized RFQ & Marketplace',
            description: 'Manage enterprise quotations, marketplace transactions, and government contracting from one unified platform.'
          },
          customWorkflow: {
            title: 'Custom Workflow Alignment',
            description: 'Partner with us to customize workflows that match your specific business operations and requirements.'
          },
          marketVisibility: {
            title: 'Competitive Market Visibility',
            description: 'Increase your market presence and connect with buyers, sellers, and partners across the solar industry.'
          },
          costSavings: {
            title: 'Cost Savings Through Automation',
            description: 'Reduce operational costs through automated vendor discovery, quotation management, and procurement workflows.'
          }
        },
        modules: {
          title: 'Multi-Module Ecosystem',
          subtitle: 'Three powerful modules designed to extend your solar operations',
          marketplace: {
            title: 'Marketplace Module',
            description: 'Buy, sell, and auction solar equipment with verified vendors.',
            features: [
              'Competitive bidding and auctions',
              'Verified buyer and seller network',
              'Secure transaction processing',
              'Product catalog management'
            ]
          },
          enterprise: {
            title: 'Enterprise Quotation System',
            description: 'Streamline your RFQ process with advanced quotation management.',
            features: [
              'Request-for-quotation management',
              'Vendor response tracking',
              'Digital agreement workflows',
              'Purchase order automation'
            ]
          },
          government: {
            title: 'Government Contracting System',
            description: 'Access government solar contracting opportunities and certified vendor networks.',
            features: [
              'Government RFQ management',
              'Certified vendor network',
              'Compliance tracking',
              'Proposal submission workflows'
            ]
          }
        },
        collaboration: {
          title: 'Partner Collaboration',
          subtitle: 'Work directly with our development team to shape EvoltBay',
          requestFeatures: {
            title: 'Request New Features',
            description: 'Partner companies can request new features and capabilities that align with their operational needs.'
          },
          influenceWorkflows: {
            title: 'Influence Workflows',
            description: 'Suggest workflow improvements and system behavior changes to match your business processes.'
          },
          guideRoadmap: {
            title: 'Guide Roadmap Priorities',
            description: 'Help prioritize platform development by sharing your business requirements and strategic goals.'
          },
          continuousCollaboration: {
            title: 'Continuous Collaboration',
            description: 'Maintain an ongoing partnership with our development team to ensure EvoltBay evolves with your needs.'
          }
        },
        cta: {
          title: 'Become a Partner',
          subtitle: 'Join solar companies that are shaping the future of solar commerce',
          explorePartnership: 'Explore Partnership Opportunities',
          requestDemo: 'Request a Demonstration',
          earlyFeedback: 'Provide Early Feedback',
          contactEmail: 'support@evoltbay.com',
          contactLabel: 'Contact us at'
        }
      },
      about: {
        title: 'What is EvoltBay?',
        description: 'EvoltBay is the leading solar energy ecommerce platform and enterprise quotation system designed to revolutionize solar equipment procurement.',
        ecommerce: {
          title: 'Solar Energy Ecommerce',
          description: 'Browse thousands of solar products from certified vendors. Buy directly or participate in auctions for the best deals.'
        },
        quotation: {
          title: 'Enterprise Quotation System',
          description: 'Advanced quotation system for managing RFQs, vendor responses, and procurement workflows efficiently.'
        },
        contracting: {
          title: 'Government Solar Contracting',
          description: 'Specialized platform for government agencies to connect with certified vendors and manage solar contracting projects.'
        }
      },
      categories: {
        title: 'Shop by Category',
        subtitle: 'Find exactly what you need for your solar project'
      },
      featured: {
        title: 'Featured Products',
        subtitle: 'Handpicked deals and trending items',
        viewAll: 'View All'
      },
      cta: {
        enterprise: {
          title: 'Scale Your Solar Projects',
          subtitle: 'Connect with certified enterprise vendors and manage large-scale solar installations.',
          exploreEnterprise: 'Explore Enterprise',
          viewRfqs: 'View My RFQs'
        },
        general: {
          title: 'Ready to Go Solar?',
          subtitle: 'Join thousands of satisfied customers who have found their perfect solar solutions on EvoltBay.',
          getStarted: 'Get Started Free',
          exploreProducts: 'Explore Products'
        }
      }
    },
    footer: {
      description: 'The premier solar energy marketplace connecting buyers and vendors for sustainable energy solutions.',
      quickLinks: 'Quick Links',
      browseProducts: 'Browse Products',
      liveAuctions: 'Live Auctions',
      categories: 'Categories',
      roiCalculator: 'ROI Calculator',
      enterpriseSolutions: 'Enterprise Solutions',
      forBusiness: 'For Business',
      becomeVendor: 'Become a Vendor',
      enterpriseBuyers: 'Enterprise Buyers',
      governmentAgencies: 'Government Agencies',
      quoteManagement: 'Quote Management',
      digitalWallet: 'Digital Wallet',
      legalSupport: 'Legal & Support',
      termsOfService: 'Terms of Service',
      privacyPolicy: 'Privacy Policy',
      contactSupport: 'Contact Support',
      legalInquiries: 'Legal Inquiries',
      helpCenter: 'Help Center',
      followUs: 'Follow us',
      allRightsReserved: 'All rights reserved.',
      empoweringSustainable: 'Empowering sustainable energy solutions worldwide.',
      legalNotice: 'All product names, logos, and brands are property of their respective owners. Use of these names does not imply endorsement.'
    },
    products: {
      title: 'Solar Products',
      subtitle: 'Buy high-quality solar equipment directly from verified sellers',
      searchPlaceholder: 'Search solar products...',
      category: 'Category',
      allCategories: 'All Categories',
      minPrice: 'Min Price',
      maxPrice: 'Max Price',
      condition: 'Condition',
      anyCondition: 'Any Condition',
      new: 'New',
      likeNew: 'Like New',
      good: 'Good',
      fair: 'Fair',
      poor: 'Poor',
      noProducts: 'No products found',
      buyFromVerified: 'Buy from verified sellers',
      filters: 'Filters',
      clearFilters: 'Clear filters',
      productsFound: 'products found',
      tryAdjustingFilters: 'Try adjusting your search criteria or filters',
      sortBy: 'Sort by',
      newest: 'Newest',
      price: 'Price',
      name: 'Name',
      highToLow: 'High to Low',
      lowToHigh: 'Low to High'
    },
    auctions: {
      title: 'Live Auctions',
      subtitle: 'Bid on solar equipment and get the best deals',
      searchPlaceholder: 'Search auctions...',
      minBid: 'Min Bid',
      activeAuction: 'Active Auction',
      auctionEnded: 'Auction Ended',
      bidNow: 'Bid Now',
      currentBid: 'Current bid',
      noAuctions: 'No auctions found',
      viewDetails: 'View Details',
      viewResult: 'View Result',
      buyNowAvailable: 'Buy Now Available',
      activeAuctionsCount: 'active auctions'
    },
    search: {
      title: 'Search Solar Products',
      searchSolarProducts: 'Search Solar Products',
      noResults: 'No products found',
      searching: 'Searching...',
      resultsFound: 'results found'
    },
    categories: {
      pageTitle: 'Categories',
      pageSubtitle: 'Explore our comprehensive range of solar equipment by category',
      searchPlaceholder: 'Search categories...',
      failedToLoad: 'Failed to load categories'
    },
    productDetail: {
      buyNow: 'Buy Now',
      placeBid: 'Place Bid',
      addToCart: 'Add to Cart',
      description: 'Description',
      seller: 'Seller',
      condition: 'Condition',
      category: 'Category',
      backToSearch: 'Back to Search',
      productNotFound: 'Product not found',
      productNotFoundDesc: "The product you're looking for doesn't exist.",
      browseProducts: 'Browse Products',
      noImageAvailable: 'No image available',
      currentBid: 'Current Bid',
      timeLeft: 'Time left',
      minimumBid: 'Minimum bid',
      buyNowPrice: 'Buy Now Price',
      price: 'Price',
      messageSeller: 'Message Seller',
      specifications: 'Specifications',
      biddingHistory: 'Bidding History',
      winning: 'Winning',
      auctionEnded: 'Auction ended',
      failedToLoad: 'Failed to load product'
    },
    gov: {
      dashboard: 'Dashboard',
      rfqs: 'RFQs',
      proposals: 'Proposals',
      vendors: 'Vendors',
      createRfq: 'Create RFQ',
      settings: 'Settings',
      registerSuccess: {
        title: 'Registration Submitted',
        defaultMessage: 'Your agency registration has been submitted for review.',
        whatHappensNext: 'What happens next?',
        step1: 'Our team will review your registration',
        step2: "You'll receive an email notification once approved",
        step3: 'Approval typically takes 1-3 business days',
        goToLogin: 'Go to Login',
        backToHome: 'Back to Home'
      }
    },
    terms: {
      title: 'Terms of Service',
      backToEvoltbay: 'Back to Evoltbay',
      lastUpdated: 'Last updated'
    },
    privacy: {
      title: 'Privacy Policy',
      backToEvoltbay: 'Back to Evoltbay',
      lastUpdated: 'Last updated'
    },
    roi: {
      title: 'Solar ROI Calculator',
      subtitle: 'Calculate your solar investment returns, energy savings, and environmental impact with our advanced ROI simulator.',
      loginRequired: 'Login Required',
      loginRequiredDesc: 'To access our advanced ROI Calculator and save your simulations, please log in to your Evoltbay account.',
      enterpriseVendorTitle: 'Enterprise Vendor Access',
      enterpriseVendorDesc: 'The ROI Calculator is designed for enterprise buyers to evaluate solar projects. As an enterprise vendor, you can access the vendor directory and manage your quote responses.',
      goToVendorDirectory: 'Go to Vendor Directory',
      signIn: 'Sign In'
    },
    nav: {
      dashboard: 'Dashboard',
      rfqs: 'RFQs',
      proposals: 'Proposals',
      vendors: 'Vendors',
      browse: 'Browse',
      auctions: 'Auctions',
      categories: 'Categories',
      enterprise: 'Enterprise',
      vendorDirectory: 'Vendor Directory',
      roiSimulator: 'ROI Simulator',
      browseRfqs: 'Browse RFQs',
      myResponses: 'My Responses',
      myRfqs: 'My RFQs',
      myQuotes: 'My Quotes',
      backToEvoltbay: 'Back to Evoltbay',
      sell: 'Sell',
      settings: 'Settings',
      signOut: 'Sign out',
      signingOut: 'Signing out...',
      agencyDashboard: 'Agency Dashboard',
      enterpriseDashboard: 'Enterprise Dashboard',
      wallet: 'Wallet',
      profiles: 'Profiles',
      company: 'Company',
      personal: 'Personal',
      profile: 'Profile',
      messages: 'Messages',
      adminDashboard: 'Admin Dashboard',
      adminSettings: 'Admin Settings',
      govAgencyApprovals: 'Gov Agency Approvals',
      myOrders: 'My Orders',
      myProducts: 'My Products',
      signIn: 'Sign in',
      signUp: 'Sign up',
      searchProducts: 'Search products...',
      cart: 'Cart'
    },
    partnership: {
      title: 'Built Through Partnership',
      subtitle: 'EvoltBay treats solar companies as strategic partners, not just customers. We collaborate directly with partners to build a platform that extends your ecosystem.',
      collaborativeDevelopment: {
        title: 'Collaborative Development',
        description: 'Partner companies work directly with the EvoltBay development team to shape the platform. You can request new features, suggest workflow improvements, and influence system behavior to match your specific business needs. This ensures EvoltBay evolves with you, reflecting real operational requirements rather than forcing you to adapt to a rigid system.'
      },
      directCollaboration: {
        title: 'Direct Collaboration',
        description: 'Work directly with our development team to shape features and workflows.'
      },
      featureRequests: {
        title: 'Feature Requests',
        description: 'Suggest improvements and new capabilities that align with your operations.'
      },
      adaptiveSystem: {
        title: 'Adaptive System',
        description: 'The platform adapts to your processes, reducing friction and accelerating innovation.'
      },
      cta: {
        title: 'Interested in Becoming a Partner?',
        description: 'Partner companies benefit from faster innovation, reduced friction, and a system that adapts to existing processes instead of forcing change. Whether you\'re a manufacturer, distributor, or enterprise buyer, we\'re building EvoltBay to extend your ecosystem—not replace it.',
        button: 'Contact Us at support@evoltbay.com',
        subtext: 'Reach out to learn more about partnership opportunities, early collaboration, or to discuss how EvoltBay can extend your solar operations.'
      }
    }
  },
  es: {
    common: {
      loading: 'Cargando...',
      error: 'Ocurrió un error',
      success: 'Éxito',
      cancel: 'Cancelar',
      save: 'Guardar',
      delete: 'Eliminar',
      edit: 'Editar',
      view: 'Ver',
      back: 'Atrás',
      next: 'Siguiente',
      previous: 'Anterior',
      search: 'Buscar',
      filter: 'Filtrar',
      sort: 'Ordenar'
    },
    navigation: {
      home: 'Inicio',
      products: 'Productos',
      auctions: 'Subastas',
      enterprise: 'Empresarial',
      government: 'Gubernamental',
      about: 'Acerca de',
      contact: 'Contacto',
      login: 'Iniciar Sesión',
      register: 'Registrarse',
      dashboard: 'Panel'
    },
    auth: {
      login: {
        title: 'Inicia sesión en tu cuenta',
        subtitle: 'O',
        emailLabel: 'Dirección de correo electrónico',
        emailPlaceholder: 'Ingresa tu correo electrónico',
        passwordLabel: 'Contraseña',
        passwordPlaceholder: 'Ingresa tu contraseña',
        rememberMe: 'Recordarme',
        forgotPassword: '¿Olvidaste tu contraseña?',
        signIn: 'Iniciar sesión',
        signingIn: 'Iniciando sesión...',
        createAccount: 'crear una cuenta nueva',
        or: 'O',
        errors: {
          fillAllFields: 'Por favor completa todos los campos',
          invalidEmail: 'Por favor ingresa una dirección de correo electrónico válida',
          loginFailed: 'Error al iniciar sesión',
          networkError: 'No se puede conectar al servicio de autenticación. Por favor verifica tu conexión a internet e intenta de nuevo.',
          serviceUnavailable: 'El servicio de autenticación no está disponible temporalmente. Por favor intenta de nuevo en un momento.',
          endpointNotFound: 'No se encontró el endpoint del servicio de autenticación. Por favor contacta al soporte si esto persiste.',
          serverError: 'Ocurrió un error en el servidor. Por favor intenta de nuevo en un momento.',
          tooManyAttempts: 'Demasiados intentos de inicio de sesión. Por favor espera antes de intentar de nuevo.',
          genericError: 'Ocurrió un error durante el inicio de sesión. Por favor intenta de nuevo.'
        }
      },
      forgotPassword: {
        title: '¿Olvidaste tu contraseña?',
        subtitle: 'Ingresa tu correo electrónico y te enviaremos instrucciones para restablecer tu contraseña.',
        backToLogin: 'Volver a Iniciar Sesión',
        checkEmail: 'Revisa tu correo',
        sentInstructions: 'Hemos enviado instrucciones de restablecimiento a',
        returnToLogin: 'Volver a Iniciar Sesión',
        emailLabel: 'Correo electrónico',
        emailPlaceholder: 'Ingresa tu correo',
        sendResetInstructions: 'Enviar Instrucciones',
        sending: 'Enviando...'
      },
      register: {
        createAccount: 'Crea tu cuenta',
        signInExisting: 'inicia sesión en tu cuenta existente',
        joinAs: 'Quiero unirme a Evoltbay como:',
        buyer: 'Comprador',
        vendor: 'Vendedor/Proveedor',
        firstName: 'Nombre',
        lastName: 'Apellido',
        confirmPassword: 'Confirmar Contraseña',
        companyInfo: 'Información de Empresa',
        businessInfo: 'Información de Negocio',
        companyName: 'Nombre de empresa',
        goToLogin: 'Ir a Iniciar Sesión',
        checkEmail: 'Revisa tu correo'
      }
    },
    home: {
      hero: {
        title: 'Extiende tu Ecosistema Solar a Través de Asociaciones',
        subtitle: 'EvoltBay es una plataforma de asociaciones que extiende tus operaciones existentes, no las reemplaza. Colabora con nuestro equipo de desarrollo para dar forma a una plataforma adaptada a las necesidades de tu negocio.',
        cta: {
          browseProducts: 'Explorar Productos',
          viewAuctions: 'Ver Subastas',
          exploreEnterprise: 'Explorar Soluciones Empresariales'
        }
      },
      features: {
        title: '¿Por qué elegir EvoltBay?',
        subtitle: 'Proporcionamos una plataforma segura, eficiente y completa para todas tus necesidades solares.',
        secureTransactions: {
          title: 'Transacciones Seguras',
          description: 'Encriptación avanzada y protección al comprador garantizan que tus transacciones sean seguras.'
        },
        qualityVerified: {
          title: 'Calidad Verificada',
          description: 'Todos los productos son verificados en calidad y autenticidad por nuestro equipo experto.'
        },
        bestPrices: {
          title: 'Mejores Precios',
          description: 'Precios competitivos a través de subastas y ventas directas de vendedores verificados.'
        }
      },
      partnership: {
        hero: {
          title: 'Construido a Través de Asociaciones',
          subtitle: 'EvoltBay trata a las empresas solares como socios estratégicos, no solo como clientes. Colaboramos directamente con los socios para construir una plataforma que extiende tu ecosistema.'
        },
        benefits: {
          title: 'Beneficios de la Asociación',
          reducedFriction: {
            title: 'Menor Fricción Operativa',
            description: 'Optimiza tus flujos de trabajo con una plataforma que se adapta a tus procesos existentes en lugar de forzar cambios.'
          },
          fasterProcurement: {
            title: 'Ciclos de Adquisición Más Rápidos',
            description: 'Acelera tu adquisición de equipos solares con gestión centralizada de RFQs y descubrimiento de proveedores.'
          },
          centralizedRfq: {
            title: 'RFQ y Mercado Centralizados',
            description: 'Gestiona cotizaciones empresariales, transacciones del mercado y contratación gubernamental desde una plataforma unificada.'
          },
          customWorkflow: {
            title: 'Alineación de Flujos de Trabajo Personalizados',
            description: 'Asóciate con nosotros para personalizar flujos de trabajo que coincidan con tus operaciones y requisitos comerciales específicos.'
          },
          marketVisibility: {
            title: 'Visibilidad Competitiva en el Mercado',
            description: 'Aumenta tu presencia en el mercado y conéctate con compradores, vendedores y socios en toda la industria solar.'
          },
          costSavings: {
            title: 'Ahorro de Costos mediante Automatización',
            description: 'Reduce los costos operativos mediante descubrimiento automatizado de proveedores, gestión de cotizaciones y flujos de trabajo de adquisición.'
          }
        },
        modules: {
          title: 'Ecosistema Multi-Módulo',
          subtitle: 'Tres módulos poderosos diseñados para extender tus operaciones solares',
          marketplace: {
            title: 'Módulo de Mercado',
            description: 'Compra, vende y subasta equipos solares con proveedores verificados.',
            features: [
              'Licitaciones y subastas competitivas',
              'Red de compradores y vendedores verificados',
              'Procesamiento seguro de transacciones',
              'Gestión de catálogo de productos'
            ]
          },
          enterprise: {
            title: 'Sistema de Cotización Empresarial',
            description: 'Optimiza tu proceso de RFQ con gestión avanzada de cotizaciones.',
            features: [
              'Gestión de solicitudes de cotización',
              'Seguimiento de respuestas de proveedores',
              'Flujos de trabajo de acuerdos digitales',
              'Automatización de órdenes de compra'
            ]
          },
          government: {
            title: 'Sistema de Contratación Gubernamental',
            description: 'Accede a oportunidades de contratación solar gubernamental y redes de proveedores certificados.',
            features: [
              'Gestión de RFQs gubernamentales',
              'Red de proveedores certificados',
              'Seguimiento de cumplimiento',
              'Flujos de trabajo de envío de propuestas'
            ]
          }
        },
        collaboration: {
          title: 'Colaboración con Socios',
          subtitle: 'Trabaja directamente con nuestro equipo de desarrollo para dar forma a EvoltBay',
          requestFeatures: {
            title: 'Solicitar Nuevas Funciones',
            description: 'Las empresas socias pueden solicitar nuevas funciones y capacidades que se alineen con sus necesidades operativas.'
          },
          influenceWorkflows: {
            title: 'Influir en Flujos de Trabajo',
            description: 'Sugiere mejoras en flujos de trabajo y cambios en el comportamiento del sistema para que coincidan con tus procesos comerciales.'
          },
          guideRoadmap: {
            title: 'Guiar Prioridades del Roadmap',
            description: 'Ayuda a priorizar el desarrollo de la plataforma compartiendo tus requisitos comerciales y objetivos estratégicos.'
          },
          continuousCollaboration: {
            title: 'Colaboración Continua',
            description: 'Mantén una asociación continua con nuestro equipo de desarrollo para asegurar que EvoltBay evolucione con tus necesidades.'
          }
        },
        cta: {
          title: 'Conviértete en Socio',
          subtitle: 'Únete a empresas solares que están dando forma al futuro del comercio solar',
          explorePartnership: 'Explorar Oportunidades de Asociación',
          requestDemo: 'Solicitar una Demostración',
          earlyFeedback: 'Proporcionar Retroalimentación Temprana',
          contactEmail: 'support@evoltbay.com',
          contactLabel: 'Contáctanos en'
        }
      },
      about: {
        title: '¿Qué es EvoltBay?',
        description: 'EvoltBay es la plataforma líder de comercio solar y sistema de cotización empresarial diseñada para revolucionar la adquisición de equipos solares.',
        ecommerce: {
          title: 'Comercio Solar',
          description: 'Navega miles de productos solares de proveedores certificados. Compra directamente o participa en subastas para las mejores ofertas.'
        },
        quotation: {
          title: 'Sistema de Cotización Empresarial',
          description: 'Sistema avanzado de cotización para gestionar RFQs, respuestas de proveedores y flujos de trabajo de adquisición de manera eficiente.'
        },
        contracting: {
          title: 'Contratación Solar Gubernamental',
          description: 'Plataforma especializada para agencias gubernamentales para conectarse con proveedores certificados y gestionar proyectos de contratación solar.'
        }
      },
      categories: {
        title: 'Comprar por Categoría',
        subtitle: 'Encuentra exactamente lo que necesitas para tu proyecto solar'
      },
      featured: {
        title: 'Productos Destacados',
        subtitle: 'Ofertas seleccionadas y artículos populares',
        viewAll: 'Ver Todo'
      },
      cta: {
        enterprise: {
          title: 'Escala tus Proyectos Solares',
          subtitle: 'Conéctate con proveedores empresariales certificados y gestiona instalaciones solares a gran escala.',
          exploreEnterprise: 'Explorar Empresarial',
          viewRfqs: 'Ver Mis RFQs'
        },
        general: {
          title: '¿Listo para Ir Solar?',
          subtitle: 'Únete a miles de clientes satisfechos que han encontrado sus soluciones solares perfectas en EvoltBay.',
          getStarted: 'Comenzar Gratis',
          exploreProducts: 'Explorar Productos'
        }
      }
    },
    footer: {
      description: 'El mercado líder de energía solar que conecta compradores y proveedores para soluciones energéticas sostenibles.',
      quickLinks: 'Enlaces Rápidos',
      browseProducts: 'Explorar Productos',
      liveAuctions: 'Subastas en Vivo',
      categories: 'Categorías',
      roiCalculator: 'Calculadora ROI',
      enterpriseSolutions: 'Soluciones Empresariales',
      forBusiness: 'Para Negocios',
      becomeVendor: 'Ser Proveedor',
      enterpriseBuyers: 'Compradores Empresariales',
      governmentAgencies: 'Agencias Gubernamentales',
      quoteManagement: 'Gestión de Cotizaciones',
      digitalWallet: 'Billetera Digital',
      legalSupport: 'Legal y Soporte',
      termsOfService: 'Términos de Servicio',
      privacyPolicy: 'Política de Privacidad',
      contactSupport: 'Contactar Soporte',
      legalInquiries: 'Consultas Legales',
      helpCenter: 'Centro de Ayuda',
      followUs: 'Síguenos',
      allRightsReserved: 'Todos los derechos reservados.',
      empoweringSustainable: 'Impulsando soluciones energéticas sostenibles en todo el mundo.',
      legalNotice: 'Todos los nombres de productos, logotipos y marcas son propiedad de sus respectivos dueños. El uso de estos nombres no implica respaldo.'
    },
    products: {
      title: 'Productos Solares',
      subtitle: 'Compra equipos solares de alta calidad directamente de vendedores verificados',
      searchPlaceholder: 'Buscar productos solares...',
      category: 'Categoría',
      allCategories: 'Todas las Categorías',
      minPrice: 'Precio Mín',
      maxPrice: 'Precio Máx',
      condition: 'Condición',
      anyCondition: 'Cualquier Condición',
      new: 'Nuevo',
      likeNew: 'Como Nuevo',
      good: 'Bueno',
      fair: 'Regular',
      poor: 'Pobre',
      noProducts: 'No se encontraron productos',
      buyFromVerified: 'Compra de vendedores verificados',
      filters: 'Filtros',
      clearFilters: 'Limpiar filtros',
      productsFound: 'productos encontrados',
      tryAdjustingFilters: 'Intenta ajustar tu búsqueda o filtros',
      sortBy: 'Ordenar por',
      newest: 'Más recientes',
      price: 'Precio',
      name: 'Nombre',
      highToLow: 'Mayor a menor',
      lowToHigh: 'Menor a mayor'
    },
    auctions: {
      title: 'Subastas en Vivo',
      subtitle: 'Puja por equipos solares y obtén las mejores ofertas',
      searchPlaceholder: 'Buscar subastas...',
      minBid: 'Puja Mín',
      activeAuction: 'Subasta Activa',
      auctionEnded: 'Subasta Finalizada',
      bidNow: 'Pujar Ahora',
      currentBid: 'Puja actual',
      noAuctions: 'No se encontraron subastas',
      viewDetails: 'Ver Detalles',
      viewResult: 'Ver Resultado',
      buyNowAvailable: 'Comprar Ahora Disponible',
      activeAuctionsCount: 'subastas activas'
    },
    search: {
      title: 'Buscar Productos Solares',
      searchSolarProducts: 'Buscar Productos Solares',
      noResults: 'No se encontraron productos',
      searching: 'Buscando...',
      resultsFound: 'resultados encontrados'
    },
    categories: {
      pageTitle: 'Categorías',
      pageSubtitle: 'Explora nuestra gama completa de equipos solares por categoría',
      searchPlaceholder: 'Buscar categorías...',
      failedToLoad: 'Error al cargar categorías'
    },
    productDetail: {
      buyNow: 'Comprar Ahora',
      placeBid: 'Realizar Puja',
      addToCart: 'Añadir al Carrito',
      description: 'Descripción',
      seller: 'Vendedor',
      condition: 'Condición',
      category: 'Categoría',
      backToSearch: 'Volver a Búsqueda',
      productNotFound: 'Producto no encontrado',
      productNotFoundDesc: 'El producto que buscas no existe.',
      browseProducts: 'Explorar Productos',
      noImageAvailable: 'Sin imagen disponible',
      currentBid: 'Puja actual',
      timeLeft: 'Tiempo restante',
      minimumBid: 'Puja mínima',
      buyNowPrice: 'Precio Comprar Ahora',
      price: 'Precio',
      messageSeller: 'Contactar Vendedor',
      specifications: 'Especificaciones',
      biddingHistory: 'Historial de Pujas',
      winning: 'Ganando',
      auctionEnded: 'Subasta finalizada',
      failedToLoad: 'Error al cargar producto'
    },
    gov: {
      dashboard: 'Panel',
      rfqs: 'RFQs',
      proposals: 'Propuestas',
      vendors: 'Proveedores',
      createRfq: 'Crear RFQ',
      settings: 'Configuración',
      registerSuccess: {
        title: 'Registro Enviado',
        defaultMessage: 'Tu registro de agencia ha sido enviado para revisión.',
        whatHappensNext: '¿Qué sigue?',
        step1: 'Nuestro equipo revisará tu registro',
        step2: 'Recibirás un correo cuando sea aprobado',
        step3: 'La aprobación suele tomar 1-3 días hábiles',
        goToLogin: 'Ir a Iniciar Sesión',
        backToHome: 'Volver al Inicio'
      }
    },
    terms: {
      title: 'Términos de Servicio',
      backToEvoltbay: 'Volver a Evoltbay',
      lastUpdated: 'Última actualización'
    },
    privacy: {
      title: 'Política de Privacidad',
      backToEvoltbay: 'Volver a Evoltbay',
      lastUpdated: 'Última actualización'
    },
    roi: {
      title: 'Calculadora ROI Solar',
      subtitle: 'Calcula retornos de inversión solar, ahorros energéticos e impacto ambiental con nuestro simulador ROI.',
      loginRequired: 'Inicio de Sesión Requerido',
      loginRequiredDesc: 'Para acceder a la Calculadora ROI y guardar simulaciones, inicia sesión en tu cuenta Evoltbay.',
      enterpriseVendorTitle: 'Acceso Proveedor Empresarial',
      enterpriseVendorDesc: 'La Calculadora ROI es para compradores empresariales. Como proveedor, accede al directorio y gestiona tus respuestas a cotizaciones.',
      goToVendorDirectory: 'Ir al Directorio de Proveedores',
      signIn: 'Iniciar Sesión'
    },
    nav: {
      dashboard: 'Panel',
      rfqs: 'RFQs',
      proposals: 'Propuestas',
      vendors: 'Proveedores',
      browse: 'Explorar',
      auctions: 'Subastas',
      categories: 'Categorías',
      enterprise: 'Empresarial',
      vendorDirectory: 'Directorio de Proveedores',
      roiSimulator: 'Simulador ROI',
      browseRfqs: 'Explorar RFQs',
      myResponses: 'Mis Respuestas',
      myRfqs: 'Mis RFQs',
      myQuotes: 'Mis Cotizaciones',
      backToEvoltbay: 'Volver a Evoltbay',
      sell: 'Vender',
      settings: 'Configuración',
      signOut: 'Cerrar sesión',
      signingOut: 'Cerrando sesión...',
      agencyDashboard: 'Panel de Agencia',
      enterpriseDashboard: 'Panel Empresarial',
      wallet: 'Billetera',
      profiles: 'Perfiles',
      company: 'Empresa',
      personal: 'Personal',
      profile: 'Perfil',
      messages: 'Mensajes',
      adminDashboard: 'Panel de Administración',
      adminSettings: 'Configuración Admin',
      govAgencyApprovals: 'Aprobaciones Agencias Gov',
      myOrders: 'Mis Pedidos',
      myProducts: 'Mis Productos',
      signIn: 'Iniciar sesión',
      signUp: 'Registrarse',
      searchProducts: 'Buscar productos...',
      cart: 'Carrito'
    },
    partnership: {
      title: 'Construido a Través de Asociaciones',
      subtitle: 'EvoltBay trata a las empresas solares como socios estratégicos, no solo como clientes. Colaboramos directamente con los socios para construir una plataforma que extiende tu ecosistema.',
      collaborativeDevelopment: {
        title: 'Desarrollo Colaborativo',
        description: 'Las empresas socias trabajan directamente con el equipo de desarrollo de EvoltBay para dar forma a la plataforma. Puedes solicitar nuevas funciones, sugerir mejoras en los flujos de trabajo e influir en el comportamiento del sistema para que coincida con las necesidades específicas de tu negocio. Esto asegura que EvoltBay evolucione contigo, reflejando requisitos operativos reales en lugar de forzarte a adaptarte a un sistema rígido.'
      },
      directCollaboration: {
        title: 'Colaboración Directa',
        description: 'Trabaja directamente con nuestro equipo de desarrollo para dar forma a funciones y flujos de trabajo.'
      },
      featureRequests: {
        title: 'Solicitudes de Funciones',
        description: 'Sugiere mejoras y nuevas capacidades que se alineen con tus operaciones.'
      },
      adaptiveSystem: {
        title: 'Sistema Adaptativo',
        description: 'La plataforma se adapta a tus procesos, reduciendo la fricción y acelerando la innovación.'
      },
      cta: {
        title: '¿Interesado en Convertirte en Socio?',
        description: 'Las empresas socias se benefician de una innovación más rápida, menos fricción y un sistema que se adapta a los procesos existentes en lugar de forzar cambios. Ya seas fabricante, distribuidor o comprador empresarial, estamos construyendo EvoltBay para extender tu ecosistema, no reemplazarlo.',
        button: 'Contáctanos en support@evoltbay.com',
        subtext: 'Comunícate para obtener más información sobre oportunidades de asociación, colaboración temprana o para discutir cómo EvoltBay puede extender tus operaciones solares.'
      }
    }
  }
}

/**
 * Get translations for a specific language
 */
export function getTranslations(lang: Language): GlobalTranslations {
  return translations[lang] || translations.en
}

/**
 * Detect user's preferred language from browser
 */
export function detectLanguage(): Language {
  if (typeof window === 'undefined') return 'en'
  
  // Check localStorage first (user preference)
  const savedLang = localStorage.getItem('app-language') as Language | null
  if (savedLang && (savedLang === 'en' || savedLang === 'es')) {
    return savedLang
  }
  
  // Fallback to coming-soon-language for backward compatibility
  const comingSoonLang = localStorage.getItem('coming-soon-language') as Language | null
  if (comingSoonLang && (comingSoonLang === 'en' || comingSoonLang === 'es')) {
    return comingSoonLang
  }
  
  // Check browser language
  const browserLang = navigator.language || (navigator as any).userLanguage || 'en'
  if (browserLang.startsWith('es')) {
    return 'es'
  }
  
  return 'en'
}

/**
 * Save language preference
 */
export function saveLanguagePreference(lang: Language): void {
  if (typeof window !== 'undefined') {
    localStorage.setItem('app-language', lang)
    // Also update coming-soon-language for backward compatibility
    localStorage.setItem('coming-soon-language', lang)
  }
}

/**
 * Get translation by key path (e.g., 'home.hero.title')
 */
export function t(lang: Language, keyPath: string): string {
  const translations = getTranslations(lang)
  const keys = keyPath.split('.')
  let value: any = translations
  
  for (const key of keys) {
    if (value && typeof value === 'object' && key in value) {
      value = value[key]
    } else {
      return keyPath // Return key path if translation not found
    }
  }
  
  return typeof value === 'string' ? value : keyPath
}
